import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, type AdminUserRow } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const SUB_LABELS: Record<string, string> = {
  inactive: "Inactiva", trialing: "En prueba", active: "Activa", past_due: "Pago vencido",
  canceled: "Cancelada", grace_period: "Periodo de gracia",
};

const fmtDate = formatDateTime;

function SendDataButton({ u }: { u: AdminUserRow }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const r = await api.post<{ sent_to: string }>(`/admin/users/${u.id}/send-data`); toast.success(`Copia enviada a ${r.data.sent_to}`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={send} disabled={busy} className="rounded-full border border-[#1B2A4A]/30 px-3 py-1 text-[11px] font-semibold text-[#1B2A4A] hover:bg-slate-50 disabled:opacity-50" data-testid={`admin-send-data-${u.email}`}>
      {busy ? "Enviando…" : "Enviar copia de datos"}
    </button>
  );
}

function StageButton({ u }: { u: AdminUserRow }) {
  const [stage, setStage] = useState(u.mentor_stage);
  const [busy, setBusy] = useState(false);
  const next = stage === "adult" ? "puppy" : "adult";
  const change = async () => {
    setBusy(true);
    try { await api.put(`/admin/users/${u.id}/mentor-stage`, { stage: next }); setStage(next); toast.success(`Dollar ahora es ${next === "adult" ? "adulto" : "cachorro"}`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-2 text-[11px]">
      <span className="rounded-full bg-[#ECE3D3] px-2 py-0.5 font-semibold text-[#1A1F2B]" data-testid={`admin-user-stage-${u.email}`}>Dollar: {stage === "adult" ? "adulto" : "cachorro"}</span>
      <button type="button" onClick={change} disabled={busy} className="rounded-full border border-[#2C4F91] px-3 py-1 font-semibold text-[#2C4F91] hover:bg-[#DFE7F3] disabled:opacity-50" data-testid={`admin-user-stage-toggle-${u.email}`}>
        Cambiar a {next === "adult" ? "adulto" : "cachorro"}
      </button>
    </span>
  );
}

function UserRow({ u }: { u: AdminUserRow }) {
  return (
    <li className="space-y-2 py-4" data-testid={`admin-user-row-${u.email}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">
            {u.name}
            {u.role === "admin" && <span className="ml-2 rounded-full bg-[#DFE7F3] px-2 py-0.5 text-[10px] font-bold uppercase text-[#16294D]">admin</span>}
            {u.is_internal && <span className="ml-2 rounded-full bg-[#ECE3D3] px-2 py-0.5 text-[10px] font-bold uppercase text-[#7A6548]" data-testid={`admin-user-internal-${u.email}`}>prueba interna</span>}
          </p>
          <p className="truncate text-xs text-slate-500">{u.email}</p>
        </div>
        <span className="shrink-0 rounded-full border border-[#DDE1E8] px-2 py-0.5 text-[11px] text-slate-600" data-testid={`admin-user-sub-${u.email}`}>
          {SUB_LABELS[u.subscription_status ?? ""] ?? "—"}
        </span>
      </div>
      <p className="text-xs text-slate-500">
        Registro: {fmtDate(u.created_at)}
        {!u.password_set && <span className="ml-2 font-semibold text-amber-700">· contraseña pendiente</span>}
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-slate-500">
        <span>Cuentas <b className="text-slate-800">{u.counts.accounts ?? 0}</b></span>
        <span>Movs. <b className="text-slate-800">{u.counts.transactions ?? 0}</b></span>
        <span>Categorías <b className="text-slate-800">{u.counts.categories ?? 0}</b></span>
        <span>Programados <b className="text-slate-800">{u.counts.scheduled_transactions ?? 0}</b></span>
        <span>Mentor mes <b className="text-slate-800">{u.mentor.messages_used_month}</b></span>
        <span>Costo IA <b className="text-slate-800">${u.mentor.estimated_cost_total.toFixed(2)} USD</b></span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <StageButton u={u} />
        {u.role !== "admin" && <SendDataButton u={u} />}
      </div>
    </li>
  );
}

export function AdminUsersPanel({ users }: { users: AdminUserRow[] }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7" data-testid="admin-users-list">
      <h3 className="font-display text-base font-bold text-[#1B2A4A]">Usuarios ({users.filter((u) => !u.is_internal).length})</h3>
      <p className="mt-1 text-xs text-slate-500">Solo datos de cuenta, conteos y uso del mentor. Sin acceso a finanzas personales.</p>
      <ul className="mt-2 divide-y divide-slate-100">
        {users.map((u) => <UserRow key={u.id} u={u} />)}
      </ul>
    </div>
  );
}
